"use client";

import { FormEvent, useState } from "react";
import { Copy, Mic, Send, Volume2, X } from "lucide-react";
import { AQUA_DRAW_MANIFEST } from "@/lib/aqua/manifest";
import { customerFirstName } from "@/lib/aqua/personality";
import type { AquaOperator } from "@/lib/aqua/types";
import { useAquaVoice } from "@/lib/aqua/useAquaVoice";

export function AquaAssistant({ operator, portalMode = false, preferredName }: { operator: AquaOperator; portalMode?: boolean; preferredName?: string }) {
  const [open, setOpen] = useState(false);
  const [modeMenu, setModeMenu] = useState(false);
  const [displayMode, setDisplayMode] = useState<"panel" | "voice" | null>(null);
  const [question, setQuestion] = useState("");
  const [notice, setNotice] = useState("");
  const customerName = customerFirstName(preferredName);
  const aqua = useAquaVoice(operator, portalMode ? customerName : "Dave");
  const startHalfPanel = async () => {
    setModeMenu(false);
    setDisplayMode("panel");
    setOpen(true);
    if (!aqua.active) await aqua.wake().catch(() => undefined);
  };

  const startVoiceOnly = async () => {
    setModeMenu(false);
    setDisplayMode("voice");
    setOpen(false);
    if (!aqua.active) await aqua.wake().catch(() => undefined);
  };

  const closeAqua = () => {
    aqua.sleep();
    setOpen(false);
    setModeMenu(false);
    setDisplayMode(null);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const request = question.trim();
    if (!request) return;
    setQuestion("");
    await aqua.askText(request).catch(() => undefined);
  };

  const copyDiagnostics = async () => {
    const payload = {
      app: AQUA_DRAW_MANIFEST,
      physical_voice_status: "not_tested",
      exported_at: new Date().toISOString(),
      events: aqua.diagnostics,
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setNotice("Diagnostics copied for reviewing.");
    window.setTimeout(() => setNotice(""), 2200);
  };

  return <>
    <button className={`aqua-orb${portalMode ? " portal-aqua-orb" : ""}${aqua.active ? " active" : ""}`} data-aqua-id="ask-aqua" data-aqua-label={portalMode ? "Ask Aqua about this project" : "Ask Aqua"} onClick={() => setModeMenu((current) => !current)} aria-label="Choose how to open Aqua" aria-expanded={modeMenu}>
      <span className="aqua-orb-mark"><img src="/aqua-homes-watermark.png" alt={portalMode ? "Aqua project assistant" : ""} /></span>
      <span><strong>{aqua.active ? statusCopy(aqua.status) : "Ask Aqua"}</strong><small>{portalMode ? "Your project assistant · tap to talk" : "Half panel or voice only"}</small></span>
      <Mic size={19}/>
    </button>

    {modeMenu ? <section className="aqua-mode-sheet" aria-label="Choose Aqua display mode">
      <header><div><span className="eyebrow">Ask Aqua</span><strong>How would you like to talk?</strong></div><button data-aqua-id="close-aqua-menu" className="icon-button" onClick={() => setModeMenu(false)} aria-label="Close Aqua choices"><X size={18}/></button></header>
      <button data-aqua-id="aqua-mode-half" className="aqua-mode-choice" onClick={startHalfPanel}><Volume2 size={19}/><span><strong>Half panel</strong><small>See the conversation while Aqua works.</small></span></button>
      <button data-aqua-id="aqua-mode-voice" className="aqua-mode-choice" onClick={startVoiceOnly}><Mic size={19}/><span><strong>Voice only</strong><small>Wake Aqua without covering the app.</small></span></button>
      {aqua.active ? <button data-aqua-id="aqua-sleep-menu" className="aqua-mode-sleep" onClick={closeAqua}>Put Aqua to sleep</button> : null}
    </section> : null}

    {displayMode === "voice" && (aqua.active || aqua.error) ? <div className={`aqua-voice-status ${aqua.status}`} role="status">
      <i className={`aqua-status-dot ${aqua.status}`}/><span><strong>Aqua</strong><small>{aqua.error || statusCopy(aqua.status)}</small></span><button data-aqua-id="aqua-sleep-status" onClick={closeAqua}>Sleep</button>
    </div> : null}

    {open ? <section className="aqua-panel half" aria-label="Aqua assistant" aria-live="polite">
      <header className="aqua-panel-head">
        <div className="aqua-avatar"><img src="/aqua-homes-watermark.png" alt="Aqua Homes" /></div>
        <div><span className="eyebrow">Aqua Core 0.19</span><h2>Aqua</h2><p><i className={`aqua-status-dot ${aqua.status}`}/>{statusCopy(aqua.status)}</p></div>
        <button data-aqua-id="close-aqua-panel" className="icon-button" onClick={closeAqua} aria-label="Close Aqua"><X size={19}/></button>
      </header>

      <div className="aqua-capability-strip"><Volume2 size={15}/><span>Tap Ask Aqua to start a secured voice session</span><b>{portalMode ? "CLIENT PORTAL" : "OWNER TEST"}</b></div>

      <div className="aqua-transcript" role="log">
        {aqua.turns.length ? aqua.turns.slice(-8).map((item) => <div className={`aqua-turn ${item.role}`} key={item.id}><span>{item.role === "assistant" ? "Aqua" : "You"}</span><p>{item.text}</p></div>) : <div className="aqua-empty"><strong>{portalMode ? <>Hi, {customerName}. I’m Aqua.</> : <>I’m ready.</>}</strong><p>{portalMode ? "I’m your project-portal instructor. Tell me what you want to do, and I’ll highlight and explain one step at a time." : "Ask about financial health, invoices, holdbacks, a client or address, or any visible field."}</p></div>}
      </div>

      {aqua.error ? <div className="aqua-error" role="alert">{aqua.error}</div> : null}
      {notice ? <div className="aqua-notice">{notice}</div> : null}

      <form className="aqua-compose" onSubmit={submit}>
        <input data-aqua-field="aqua-message" data-aqua-label="Message Aqua" value={question} onChange={(event)=>setQuestion(event.target.value)} placeholder="Ask Aqua or give a safe app command" aria-label="Message Aqua" />
        <button data-aqua-id="send-aqua-message" type="submit" disabled={!question.trim() || aqua.status === "connecting"} aria-label="Send to Aqua"><Send size={18}/></button>
      </form>

      <footer className="aqua-panel-foot">
        <button data-aqua-id="toggle-aqua-listening" className="secondary-button" onClick={aqua.active ? closeAqua : startHalfPanel}><Mic size={15}/>{aqua.active ? "Go to sleep" : "Start listening"}</button>
        <button data-aqua-id="copy-aqua-diagnostics" className="aqua-diagnostics" onClick={copyDiagnostics}><Copy size={14}/>Copy diagnostics for reviewing</button>
      </footer>
      <small className="aqua-truth">{portalMode ? "Only this client portal’s records are available. Invoice approval and any holdback require your confirmation." : "Registered owner actions are active. Money changes and deletion always require confirmation."} Physical voice remains unapproved until tested on this device.</small>
    </section> : null}
  </>;
}

function statusCopy(status: ReturnType<typeof useAquaVoice>["status"]) {
  if (status === "standby") return "Standby";
  if (status === "connecting") return "Securing voice…";
  if (status === "listening") return "Listening";
  if (status === "thinking") return "Thinking";
  if (status === "speaking") return "Speaking";
  return "Needs attention";
}
